// The Pipeline: a column per stage, a card per project with the contract's
// value, the savings target, and under them the user's open action items,
// with a line for the ones on someone else.

import { FC } from "react";
import { clsx } from "clsx";
import { StatusDot, Tooltip } from "../../../../design-system";
import { contractValue, dealById, isNextMove, vendorOfDeal } from "../model/derived";
import { isDrillableProject } from "../model/fixtures";
import { daysUntil, longDate, money, monthShort } from "../model/format";
import type { ProjectStage, UsageProject } from "../model/types";
import { usageProjectPath } from "../paths";
import { ProjectEntityCell } from "../components/cells";
import classes from "./PipelineBoard.module.less";

/** The board's columns, in stage order; a playbook in hand means the negotiation is on. */
const COLUMNS: { key: string; label: string; stages: ProjectStage[]; noTarget?: boolean }[] = [
    { key: "proposed", label: "Opportunity", stages: ["proposed"] },
    // No target before the analysis is published, so the column shows only its value.
    { key: "usage_analysis", label: "Usage analysis", stages: ["usage_analysis"], noTarget: true },
    { key: "negotiating", label: "Negotiating", stages: ["playbook", "negotiating"] },
    // After signing: what the closed projects realized.
    { key: "realized", label: "Realized savings", stages: ["closed"] },
];

/** The card's green number: the target while open, what was realized once closed. */
const savingsOf = (project: UsageProject) =>
    project.stage === "closed" ? (project.realizedSavings ?? 0) : project.estSavings;

/** How pressing a due date is: past it, within the week, or later. */
const urgencyOf = (iso: string): "red" | "orange" | "grey" => {
    const days = daysUntil(iso);
    return days < 0 ? "red" : days <= 7 ? "orange" : "grey";
};

/** The due date as a tag: "Overdue: 3 days", "Today", or the date; colored by its urgency. */
const DueTag: FC<{ iso: string }> = ({ iso }) => {
    const days = daysUntil(iso);
    const text =
        days < 0
            ? `Overdue: ${-days} ${-days === 1 ? "day" : "days"}`
            : days === 0
              ? "Today"
              : `${monthShort(iso.slice(0, 7))} ${Number(iso.slice(8, 10))}`;
    return (
        <Tooltip title={`Due ${longDate(iso)}`}>
            <span className={clsx(classes.due, classes[`due--${urgencyOf(iso)}`])}>{text}</span>
        </Tooltip>
    );
};

const ProjectCard: FC<{ project: UsageProject }> = ({ project }) => {
    const deal = dealById(project.dealId)!;
    const open = project.actionItems.filter((item) => item.status === "open");
    const items = open.filter(isNextMove).sort((a, b) => a.due.localeCompare(b.due));
    const mine = items.length;
    return (
        <div className={classes.card}>
            <div className={classes.card__head}>
                <ProjectEntityCell
                    project={project}
                    vendor={vendorOfDeal(deal)}
                    href={isDrillableProject(project.id) ? usageProjectPath(project.id) : undefined}
                />
                <span className={classes.card__side}>
                    <span className={classes.card__tcv} title="Total contract value">
                        {money(contractValue(deal))}
                    </span>
                    {savingsOf(project) > 0 ? (
                        <span className={classes.card__savings}>{money(savingsOf(project))}</span>
                    ) : (
                        <span className={classes.card__pending}>Pending</span>
                    )}
                </span>
            </div>
            {mine > 0 && (
                <div className={classes.items}>
                    {items.map((item) => (
                        <div key={item.id} className={classes.item}>
                            <StatusDot color={urgencyOf(item.due)} className={classes.item__title}>
                                {item.title}
                            </StatusDot>
                            <DueTag iso={item.due} />
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export const PipelineBoard: FC<{ projects: UsageProject[] }> = ({ projects }) => (
    <div className={classes.board}>
        {COLUMNS.map((column) => {
            const inColumn = projects.filter((p) => column.stages.includes(p.stage));
            const target = inColumn.reduce((sum, p) => sum + savingsOf(p), 0);
            const tcv = inColumn.reduce((sum, p) => sum + contractValue(dealById(p.dealId)!), 0);
            return (
                <section key={column.key} className={classes.column} aria-label={column.label}>
                    <h3 className={classes.column__title}>
                        {column.label}
                        <span className={classes.column__count}>{inColumn.length}</span>
                        {inColumn.length > 0 && (
                            <span
                                className={classes.column__totals}
                                title="Savings / total contract value"
                            >
                                {!column.noTarget && (
                                    <>
                                        <span className={classes.column__target}>
                                            {money(target)}
                                        </span>
                                        {" / "}
                                    </>
                                )}
                                {money(tcv)}
                            </span>
                        )}
                    </h3>
                    {inColumn.map((project) => (
                        <ProjectCard key={project.id} project={project} />
                    ))}
                </section>
            );
        })}
    </div>
);
